/** Ferien beim Planen eines Einsatzes aussparen.
 *
 *  Reine Wochenrechnung ohne DOM. Wochen sind Indizes im Planungszeitraum wie im
 *  Grid: ein Einsatz belegt startWeek bis startWeek + durationWeeks - 1, Ferien
 *  belegen startWeek bis endWeek einschliesslich.
 */

import type { Assignment, Vacation } from "../types";
import { vacationAppliesTo } from "./vacations";

export interface WeekSpan { startWeek: number; durationWeeks: number; }

/** Eigene Ferien des Schuelers und Ferien fuer alle. Ueber vacationAppliesTo, damit
 *  Schueler, die im Zeitraum von gemeinsamen Ferien ausgenommen sind, hier nicht doch
 *  Ferien ausgespart bekommen; Raster, Export und Auto-Planung pruefen genauso. */
const appliesTo = vacationAppliesTo;

export function studentVacationWeeks(vacations: Vacation[], studentId: number): Set<number> {
  const weeks = new Set<number>();
  for (const v of vacations) {
    if (!appliesTo(v, studentId)) continue;
    for (let w = v.startWeek; w <= v.endWeek; w++) weeks.add(w);
  }
  return weeks;
}

export function spanHitsVacation(span: WeekSpan, vacWeeks: Set<number>): boolean {
  for (let w = span.startWeek; w < span.startWeek + span.durationWeeks; w++) {
    if (vacWeeks.has(w)) return true;
  }
  return false;
}

/** Ferien, die in den Einsatz fallen, fuer den Text der Rueckfrage. Dieselben Ferien
 *  koennen doppelt vorliegen (eigene Zeile und fuer alle), deshalb nach Name und
 *  Wochen zusammengefasst. */
export function vacationsInSpan(vacations: Vacation[], studentId: number, span: WeekSpan): Vacation[] {
  const end = span.startWeek + span.durationWeeks - 1;
  const seen = new Set<string>();
  return vacations
    .filter(v => appliesTo(v, studentId) && v.startWeek <= end && v.endWeek >= span.startWeek)
    .sort((a, b) => a.startWeek - b.startWeek || a.endWeek - b.endWeek)
    .filter(v => {
      const key = `${v.name}|${v.startWeek}|${v.endWeek}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

/** Fasst aufsteigende Wochen zu zusammenhaengenden Teilen zusammen. */
function toSpans(weeks: number[]): WeekSpan[] {
  const parts: WeekSpan[] = [];
  for (const w of weeks) {
    const last = parts[parts.length - 1];
    if (last && last.startWeek + last.durationWeeks === w) last.durationWeeks++;
    else parts.push({ startWeek: w, durationWeeks: 1 });
  }
  return parts;
}

/** Kuerzen: das Ende bleibt, die Ferienwochen fallen weg. Leer, wenn der Einsatz ganz
 *  in den Ferien liegt. Was hinter dem Zeitraum laege, entfaellt wie beim Verlaengern. */
export function splitShorten(span: WeekSpan, vacWeeks: Set<number>, totalWeeks: number): WeekSpan[] {
  const weeks: number[] = [];
  const end = Math.min(span.startWeek + span.durationWeeks, totalWeeks);
  for (let w = span.startWeek; w < end; w++) {
    if (!vacWeeks.has(w)) weeks.push(w);
  }
  return toSpans(weeks);
}

/** Verlaengern: die Wochenzahl bleibt, Ferienwochen werden uebersprungen, auch mehrere
 *  Ferienbloecke nacheinander. Was hinter das Ende des Zeitraums fiele, entfaellt und
 *  steht in missingWeeks. */
export function splitExtend(
  span: WeekSpan, vacWeeks: Set<number>, totalWeeks: number,
): { parts: WeekSpan[]; missingWeeks: number } {
  const weeks: number[] = [];
  for (let w = span.startWeek; w < totalWeeks && weeks.length < span.durationWeeks; w++) {
    if (!vacWeeks.has(w)) weeks.push(w);
  }
  return { parts: toSpans(weeks), missingWeeks: span.durationWeeks - weeks.length };
}

/** Wochen, die erst durch das Aufteilen belegt werden und dort auf einen anderen
 *  Einsatz des Schuelers treffen. Ueberschneidungen im eingegebenen Bereich hat der
 *  Benutzer selbst gewaehlt und sind deshalb kein Hinweis wert. */
export function newOverlaps(
  parts: WeekSpan[], original: WeekSpan, assignments: Assignment[], studentId: number, excludeId?: number,
): WeekSpan[] {
  const others = assignments.filter(a => +a.studentId === +studentId && (excludeId === undefined || +a.id !== +excludeId));
  const origEnd = original.startWeek + original.durationWeeks;
  const hits: number[] = [];
  for (const p of parts) {
    for (let w = p.startWeek; w < p.startWeek + p.durationWeeks; w++) {
      if (w >= original.startWeek && w < origEnd) continue;
      if (others.some(a => a.startWeek <= w && a.startWeek + a.durationWeeks > w)) hits.push(w);
    }
  }
  return toSpans(hits);
}

export function sameParts(a: WeekSpan[], b: WeekSpan[]): boolean {
  return a.length === b.length
    && a.every((p, i) => p.startWeek === b[i].startWeek && p.durationWeeks === b[i].durationWeeks);
}
